import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useDispatch } from 'react-redux';
import { UserContext } from './UserContext';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import axiosInstance from '../../services/axios/axios';
import { getImpersonationData } from 'components/Navigation/AppBar/ImpersonationIcon/_utils';
import {
	KEY_SESSION,
	KEY_SESSION_ASPDOTNET,
} from 'components/Navigation/AppBar/ImpersonationIcon/_constants';
import { getSession } from 'store/utility';
import { addMessage } from '../../store/ancillary/actions';

export const ERROR_MESSAGE_USER =
	'An error has occurred while trying to identify the logged-in user.';

export const UserProvider = ({ children }) => {
	const { accounts, inProgress } = useMsal();
	const environment = process.env.REACT_APP_IMS_ENV ?? 'development';
	const dispatch = useDispatch();

	const [sUserInfo, setUserInfo] = useState(null);

	/**
	 * @type {import('./UserContext').ContextUser}
	 */
	const valueContext = useMemo(() => {
		return {
			userInfo: sUserInfo,
			setUserInfo: setUserInfo,
		};
	}, [sUserInfo]);

	/**
	 * Effect executing when an authentication interaction is not in progress and signed-in user information is available.
	 * Retrieves IMS metadata based on signed-in user's UPN and exposes via React context.
	 * Conditionally retrieves impersonation metadata given presence of ASP.NET or browser session values, allowing for impersonation to be preserved
	 * through page reloads.
	 */
	useEffect(() => {
		let ignore = false;

		const getUserData = async () => {
			try {
				const responseUser = await axiosInstance.get(
					'api/Identity/LoginIdentity',
					{
						params: {
							upn: accounts[0].username,
						},
					}
				);

				if (responseUser && responseUser.data) {
					const user = responseUser.data.payload;
					let impersonationUser = null;
					user.isDEVTeam =
						user?.Roles?.findIndex(
							role => role.Full === 'DEV Team' || role.ID === 18
						) > -1;
					if (!!responseUser.data.messages) {
						responseUser.data.messages.forEach(message => {
							dispatch(addMessage(message));
						});
					}

					// preserve impersonation session in case of site refresh
					let identifierImpersonation = await getSession(
						KEY_SESSION_ASPDOTNET
					);
					identifierImpersonation =
						identifierImpersonation ??
						sessionStorage.getItem(KEY_SESSION);

					if (!!identifierImpersonation) {
						impersonationUser = await getImpersonationData(
							identifierImpersonation,
							dispatch
						);
					}

					if (!ignore) {
						setUserInfo({
							allowImpersonation:
								environment === 'development' ||
								environment === 'test',
							isImpersonating: false,
							userProfile: user,
							impersonationProfile: impersonationUser,
						});
					}
				}
			} catch (error) {
				dispatch(
					addMessage({
						Key: 3,
						Value: ERROR_MESSAGE_USER,
					})
				);
				setUserInfo(null);
			}
		};

		if (inProgress === InteractionStatus.None && accounts?.[0]?.username) {
			getUserData();
		}

		return () => {
			ignore = true;
		};
	}, [accounts, inProgress, environment, dispatch]);

	/**
	 * Effect executing when no signed-in user information is available.
	 * Syncs React context with MSAL state.
	 */
	useEffect(() => {
		if (accounts?.length < 1) {
			setUserInfo(null);
		}
	}, [accounts]);

	return (
		<UserContext.Provider value={valueContext}>
			<WithLoadingHandler
				isLoading={sUserInfo === null || accounts.length < 1}
				showChildren={sUserInfo !== null}
			>
				{children}
			</WithLoadingHandler>
		</UserContext.Provider>
	);
};
